import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// material-ui
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  CircularProgress,
  FormLabel
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project import
// import useAuth from 'hooks/useAuth';
import useScriptRef from 'hooks/useScriptRef';
import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';

import AuthService from 'services/auth';
// assets
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { t } from 'i18next';
import useMain from 'hooks/useMain';
import { handleAsyncCall, versionCheckAndRefresh } from 'utils/common';
import { useCheckLoginResult } from 'hooks/useCheckLoginResult';
import LoadingButton from 'components/@extended/LoadingButton';

// ============================|| FIREBASE - LOGIN ||============================ //

const AuthLogin = () => {
  // const { isLoggedIn } = useAuth();
  const { t } = useMain();

  const scriptedRef = useScriptRef();
  const handleLogin = useCheckLoginResult();

  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    versionCheckAndRefresh();
  }, []);

  return (
    <>
      <Formik
        initialValues={{
          username: '',
          password: '',
          submit: null
        }}
        validationSchema={Yup.object().shape({
          username: Yup.string().max(255).required('Username is required'),
          password: Yup.string().max(255).required('Password is required')
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            if (values.username !== undefined && values.password !== undefined) {
              handleAsyncCall({
                service: AuthService.login,
                params: [values.username, values.password],
                // setDetailBlock: setBlock,
                successCb: (res) => {
                  handleLogin({ res, setLoginFailMessage: () => {} });
                },
                systemErrorCb: () => {
                  setStatus({ success: false });
                  setErrors({ submit: err.message });
                  setSubmitting(false);
                },
                setDetailBlock: setLoading
              });
            }
          } catch (err) {
            console.error(err);
            if (scriptedRef.current) {
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setLoading(false);
              setSubmitting(false);
            }
          }
        }}
      >
        {/* revise inputlabel to formlabel */}
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <FormLabel htmlFor="username-login">{t('USERNAME')}</FormLabel>
                  <OutlinedInput
                    id="username-login"
                    type="username"
                    value={values.username}
                    name="username"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter username address"
                    fullWidth
                    error={Boolean(touched.username && errors.username)}
                    sx={{ borderRadius: '12px' }}
                  />
                  {touched.username && errors.username && (
                    <FormHelperText error id="standard-weight-helper-text-username-login">
                      {errors.username}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <FormLabel htmlFor="password-login"> {t('PASSWORD')}</FormLabel>
                  <OutlinedInput
                    fullWidth
                    color={'primary'}
                    error={Boolean(touched.password && errors.password)}
                    id="password-login"
                    type={showPassword ? 'text' : 'password'}
                    value={values.password}
                    name="password"
                    onBlur={(event) => {
                      handleBlur(event);
                    }}
                    onChange={handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          color="secondary"
                        >
                          {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="Enter password"
                    sx={{ borderRadius: '12px' }}
                  />

                  {touched.password && errors.password && (
                    <FormHelperText error id="standard-weight-helper-text-password-login">
                      {errors.password}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sx={{ mt: -1 }}>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                  {/* <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(event) => setChecked(event.target.checked)}
                        name="checked"
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="h6">Keep me sign in</Typography>}
                  /> */}
                </Stack>
              </Grid>
              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              <Grid item xs={12}>
                <AnimateButton>
                  <Button
                    disableElevation
                    disabled={isSubmitting || loading}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: '12px', backgroundImage: 'linear-gradient(98deg, #91c7b9 -8%, #4da091 77%)' }}
                  >
                    {loading || isSubmitting ? <CircularProgress /> : t('LOGIN')}
                  </Button>
                </AnimateButton>
                <Link
                  variant="h6"
                  component={RouterLink}
                  to={'/forgotPw'}
                  color="text.primary"
                  sx={{ color: '#bfbfbf', textDecoration: 'underline' }}
                >
                  {t('FORGOT_PW')}
                </Link>
              </Grid>
              {/* <Grid item xs={12}>
                <Divider>
                  <Typography variant="caption"> Login with</Typography>
                </Divider>
              </Grid>
              <Grid item xs={12}>
                <FirebaseSocial />
              </Grid> */}
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', direction: 'row', justifyContent: 'center' }}>
              <Link variant="h6" component={RouterLink} to={'/install_tutorial'}>
                {t('INSTALL_APP_LINK')}
              </Link>
            </Grid>
          </form>
        )}
      </Formik>
    </>
  );
};

export default AuthLogin;
